import { useAuth } from '@clerk/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeleteAccount, useExportAccount } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Input } from '@/design-system/primitives/Input/Input';
import { toast } from '@/design-system/primitives/Toast/Toast';
import styles from './PrivacyDialogs.module.css';

// Vite inlines this constant; in production it's `false` and the Clerk
// signOut() path runs. In Playwright (VITE_E2E_AUTH_BYPASS=1) Clerk isn't
// mounted, so we skip the signOut call and rely on the navigate alone.
const E2E_BYPASS = import.meta.env.VITE_E2E_AUTH_BYPASS === '1';

/**
 * GDPR Article 20 — data portability. Confirms the user wants the download,
 * shows a spinner while the backend streams the ZIP, then closes. The
 * actual file download is dispatched by `useExportAccount`.
 */
export function ExportAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const mutation = useExportAccount();

  const handleConfirm = async () => {
    await mutation.mutateAsync();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent width={480}>
        <DialogTitle>Download mijn data</DialogTitle>
        <DialogDescription>
          We bundelen al je projectdata in een ZIP: documenten, beslissingen, mails, foto's,
          facturen, taken en het activiteitenlog.
        </DialogDescription>
        <DialogBody>
          <ul className={styles.bullets}>
            <li>Het bestand wordt direct in je browser gedownload.</li>
            <li>Bevat geen wachtwoorden of API-keys (niet bewaard in deze app).</li>
            <li>Conform GDPR Artikel 20 — overdraagbaarheid van gegevens.</li>
          </ul>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Btn type="button">Annuleer</Btn>
          </DialogClose>
          <Btn
            type="button"
            variant="primary"
            onClick={handleConfirm}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Bezig…' : 'Download ZIP'}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// The literal string the user must type to enable the destructive button.
// Hardcoded uppercase Dutch so it survives translation; if the surface ever
// needs i18n the string is also exported via aria-label so screen readers
// announce the exact required value.
const CONFIRM_TOKEN = 'VERWIJDER';

/**
 * GDPR Article 17 — right to erasure. Type-to-confirm flow:
 *
 * 1. User clicks "Verwijder account".
 * 2. Dialog renders a warning + an input the user must fill with
 *    `VERWIJDER` (exact-match, case-sensitive).
 * 3. Destructive button enables only once the token matches.
 * 4. On confirm: call `deleteAccount`, then Clerk `signOut`, then redirect
 *    to /sign-in with a goodbye toast.
 *
 * If anything fails between deleteAccount and signOut, we still attempt the
 * redirect — Clerk's session is what gates the app, and leaving the user on
 * a deleted-account dashboard is worse than a stale toast.
 */
export function DeleteAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const mutation = useDeleteAccount();
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  // useAuth is safe to call here unconditionally — Clerk provides `signOut`
  // as a stable function reference. In the E2E_BYPASS path we still call
  // useAuth() but skip the signOut() invocation.
  // biome-ignore lint/correctness/useHookAtTopLevel: useAuth is called unconditionally; the conditional only branches over the *result*, not whether the hook fires.
  const { signOut } = useAuth();

  const canConfirm = token === CONFIRM_TOKEN && !mutation.isPending;

  const handleConfirm = async () => {
    if (token !== CONFIRM_TOKEN) return;
    await mutation.mutateAsync();
    onOpenChange(false);
    toast.success('Account verwijderd. Tot ziens.');
    try {
      if (!E2E_BYPASS && typeof signOut === 'function') {
        await signOut();
      }
    } catch {
      // Sign-out best-effort: a failure here doesn't make the delete-account
      // call any less successful, so swallow and proceed to redirect.
    }
    navigate('/sign-in');
  };

  // Reset the token whenever the dialog closes so a re-open requires the
  // user to retype.
  const handleOpenChange = (next: boolean) => {
    if (!next) setToken('');
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent width={480}>
        <DialogTitle>Account onomkeerbaar verwijderen</DialogTitle>
        <DialogDescription>
          Deze actie is <strong>onomkeerbaar</strong>. Alle projectdata, documenten, mails en
          facturen worden definitief verwijderd. Conform GDPR Artikel 17 — recht op vergetelheid.
        </DialogDescription>
        <DialogBody>
          <p className={styles.warning}>
            Typ <span className={`mono ${styles.token}`}>{CONFIRM_TOKEN}</span> hieronder om te
            bevestigen.
          </p>
          <Input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={CONFIRM_TOKEN}
            aria-label={`Typ ${CONFIRM_TOKEN} om te bevestigen`}
            // biome-ignore lint/a11y/noAutofocus: the field is the dialog's primary action
            autoFocus
            autoComplete="off"
            className="mono"
          />
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Btn type="button">Annuleer</Btn>
          </DialogClose>
          <Btn type="button" variant="accent" onClick={handleConfirm} disabled={!canConfirm}>
            {mutation.isPending ? 'Bezig…' : 'Verwijder onomkeerbaar'}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
